import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

interface LoadingButtonProps extends ButtonProps {
  /**
   * Loading state - shows spinner and disables button
   */
  isLoading?: boolean;

  /**
   * Text to show while loading (optional)
   * If not provided, will show the original children with a spinner
   */
  loadingText?: string;

  /**
   * Custom loading icon (optional)
   * Defaults to Loader2 from lucide-react
   */
  loadingIcon?: React.ReactNode;
}

/**
 * LoadingButton Component
 *
 * Enhanced button with built-in loading state management.
 * Shows spinner icon and optional loading text while loading.
 * Automatically disables the button during loading.
 *
 * @example
 * // Basic usage
 * <LoadingButton isLoading={isSubmitting}>
 *   Submit
 * </LoadingButton>
 *
 * @example
 * // With custom loading text
 * <LoadingButton isLoading={isSubmitting} loadingText="Enviando...">
 *   Enviar
 * </LoadingButton>
 *
 * @example
 * // With form submission
 * <LoadingButton
 *   isLoading={isSubmitting}
 *   loadingText="Guardando..."
 *   type="submit"
 * >
 *   Guardar
 * </LoadingButton>
 */
export const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({
    children,
    className,
    isLoading = false,
    loadingText,
    loadingIcon,
    disabled,
    ...props
  }, ref) => {
    return (
      <Button
        ref={ref}
        className={cn(className)}
        disabled={isLoading || disabled}
        {...props}
      >
        {isLoading ? (
          <>
            {loadingIcon || <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loadingText || children}
          </>
        ) : (
          children
        )}
      </Button>
    );
  }
);

LoadingButton.displayName = 'LoadingButton';

/**
 * Inline Spinner Component
 *
 * Small spinner for inline loading states (e.g., within text, buttons, badges)
 *
 * @example
 * <InlineSpinner />
 * <InlineSpinner className="text-blue-600" size="sm" />
 */
interface InlineSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export function InlineSpinner({ size = 'sm', className }: InlineSpinnerProps) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  return (
    <Loader2
      className={cn(
        'animate-spin',
        sizeClasses[size],
        className
      )}
      aria-label="Loading"
    />
  );
}

/**
 * Full Page Spinner Component
 *
 * Centered full-page loading spinner with optional message
 * Use for page-level loading states
 *
 * @example
 * <FullPageSpinner />
 * <FullPageSpinner message="Cargando propiedades..." />
 */
interface FullPageSpinnerProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function FullPageSpinner({ message = 'Cargando...', size = 'lg' }: FullPageSpinnerProps) {
  const sizeClasses = {
    sm: 'h-12 w-12 border-2',
    md: 'h-16 w-16 border-2',
    lg: 'h-20 w-20 border-4',
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50"
      role="status"
      aria-live="polite"
    >
      <div className="text-center space-y-4">
        <div className={cn(
          'inline-block animate-spin rounded-full border-primary border-t-transparent',
          sizeClasses[size]
        )} />
        <p className="text-lg text-gray-600 font-medium">{message}</p>
      </div>
    </div>
  );
}

/**
 * Page Section Spinner Component
 *
 * Loading spinner for page sections (not full page)
 * Maintains section layout while showing loading state
 *
 * @example
 * <SectionSpinner />
 * <SectionSpinner message="Cargando propiedades destacadas..." />
 */
interface SectionSpinnerProps {
  message?: string;
  className?: string;
}

export function SectionSpinner({ message = 'Cargando...', className }: SectionSpinnerProps) {
  return (
    <div className={cn('py-20 flex items-center justify-center', className)}>
      <div className="text-center space-y-4">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent" />
        <p className="text-base text-gray-600">{message}</p>
      </div>
    </div>
  );
}

/**
 * Button Group with Loading State
 *
 * Wrapper for button groups that disables all buttons during loading
 *
 * @example
 * <LoadingButtonGroup isLoading={isProcessing}>
 *   <Button>Cancelar</Button>
 *   <Button>Guardar</Button>
 * </LoadingButtonGroup>
 */
interface LoadingButtonGroupProps {
  children: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

export function LoadingButtonGroup({ children, isLoading = false, className }: LoadingButtonGroupProps) {
  return (
    <div className={cn('flex gap-2', className)}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            disabled: isLoading || child.props.disabled,
          });
        }
        return child;
      })}
    </div>
  );
}
